import { api } from '@/lib/api';

// ============== Types ==============

export interface ValidateLinkResponse {
  accountId: string;
  account: {
    id: string;
    name: string;
    accountType: string;
    document?: {
      type: 'cpf' | 'cnpj';
      value: string;
    };
    tradingName?: string;
    companyName?: string;
    taxId?: string;
    email?: string;
    phone?: string;
    simeiSince?: string;
  };
  creditAnalysis?: {
    id: string;
    status: string;
    proposalId?: string;
    accountId: string;
    createdAt: string;
    updatedAt: string;
  } | null;
}

export interface DocumentSlotState {
  fieldId: string;
  label: string;
  required: boolean;
  status: 'approved' | 'rejected' | 'pending' | 'none';
  rejectionReason?: string;
  uploadedFileName?: string;
  uploadedAt?: string;
}

export interface GetDocumentStateResponse {
  documents: Array<{
    id: string;
    fieldId?: string;
    fileName: string;
    contentType?: string;
    sizeInBytes: number;
    createdAt: string;
    reviewStatus: 'approved' | 'rejected' | null;
    rejectionReason: string | null;
    reviewedAt: string | null;
  }>;
  creditAnalysis: {
    id: string;
    status: string;
    proposalId?: string;
    accountId: string;
    createdAt: string;
    updatedAt: string;
  } | null;
}

export interface FileUpload {
  fieldId: string;
  fileName: string;
  key: string;
  sizeInBytes: number;
  contentType?: string;
  eTag?: string;
}

export interface UploadDocumentsResponse {
  success: boolean;
  creditAnalysis?: {
    id: string;
    status: string;
    proposalId?: string;
    accountId: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface ExternalApiError {
  statusCode: number;
  message: string;
  code?: 'EXPIRED' | 'INVALID' | 'EMAIL_NOT_ALLOWED' | 'NOT_FOUND';
}

// ============== API Client ==============

export const externalApi = {
  /**
   * Validates an external link and returns account + credit analysis data
   * GET /external/validate/:linkId?email=...
   */
  validateLink: async (linkId: string, email: string): Promise<ValidateLinkResponse> => {
    const { data } = await api.get<ValidateLinkResponse>(`/external/validate/${linkId}`, {
      params: { email },
    });
    return data;
  },

  /**
   * Gets the current document state for an external link
   * GET /external/:linkId/documents?email=...
   */
  getDocumentState: async (linkId: string, email: string): Promise<GetDocumentStateResponse> => {
    const { data } = await api.get<GetDocumentStateResponse>(`/external/${linkId}/documents`, {
      params: { email },
    });
    return data;
  },

  /**
   * Uploads documents for an external link
   * POST /external/:linkId/documents?email=...
   */
  uploadDocuments: async (
    linkId: string,
    email: string,
    files: FileUpload[],
  ): Promise<UploadDocumentsResponse> => {
    const { data } = await api.post<UploadDocumentsResponse>(
      `/external/${linkId}/documents`,
      { files },
      {
        params: { email },
      },
    );
    return data;
  },
};
